<template>
  <div
    class="notification-item"
    :class="{ 'notification-item--unread': !notification.isRead }"
    @click="handleClick"
  >
    <div class="notification-item__indicator">
      <div v-if="!notification.isRead" class="notification-item__dot" />
    </div>

    <div class="notification-item__icon">
      <component :is="iconComponent" :size="20" />
    </div>

    <div class="notification-item__content">
      <div class="notification-item__title">{{ notification.title }}</div>
      <div class="notification-item__body">{{ notification.body }}</div>
      <div class="notification-item__meta">
        <span class="notification-item__time">{{ formattedTime }}</span>
        <span v-if="notification.channelsSent.length" class="notification-item__channels">
          <span
            v-for="ch in notification.channelsSent"
            :key="ch"
            class="notification-item__channel-badge"
          >
            {{ ch }}
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  MessageSquareIcon,
  BellIcon,
  CalendarIcon,
  UsersIcon,
  BriefcaseIcon,
  CopyIcon,
} from 'lucide-vue-next'
import type { Notification } from '../types/notifications.types'

const props = defineProps<{
  notification: Notification
}>()

const emit = defineEmits<{
  click: [notification: Notification]
}>()

const { t } = useI18n()

const iconComponent = computed(() => {
  switch (props.notification.notificationType) {
    case 'unread_message':
      return MessageSquareIcon
    case 'calendar_reminder':
      return CalendarIcon
    case 'client_update':
      return UsersIcon
    case 'deal_update':
      return BriefcaseIcon
    case 'agent_lead_duplicate':
      return CopyIcon
    default:
      return BellIcon
  }
})

const formattedTime = computed(() => {
  if (!props.notification.createdAt) return ''
  const date = new Date(props.notification.createdAt)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMin < 1) return t('notifications.time.justNow')
  if (diffMin < 60) return t('notifications.time.minutesAgo', { n: diffMin })
  if (diffHours < 24) return t('notifications.time.hoursAgo', { n: diffHours })
  if (diffDays < 7) return t('notifications.time.daysAgo', { n: diffDays })

  return date.toLocaleDateString()
})

function handleClick() {
  emit('click', props.notification)
}
</script>

<style scoped>
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm, 0.5rem);
  padding: var(--spacing-sm, 0.75rem) var(--spacing-md, 1rem);
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid var(--color-border-light);
}

.notification-item:hover {
  background: var(--color-background-secondary);
}

.notification-item--unread {
  background: var(--color-primary-bg, rgba(59, 130, 246, 0.04));
}

.notification-item--unread:hover {
  background: var(--color-primary-bg-hover, rgba(59, 130, 246, 0.08));
}

.notification-item__indicator {
  width: 8px;
  flex-shrink: 0;
  padding-top: 6px;
}

.notification-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-primary);
}

.notification-item__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md, 0.5rem);
  background: var(--color-background-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
}

.notification-item--unread .notification-item__icon {
  background: var(--color-primary-light, rgba(59, 130, 246, 0.1));
  color: var(--color-primary);
}

.notification-item__content {
  flex: 1;
  min-width: 0;
}

.notification-item__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.notification-item__body {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-item__meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm, 0.5rem);
  margin-top: 4px;
}

.notification-item__time {
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
}

.notification-item__channels {
  display: flex;
  gap: 4px;
}

.notification-item__channel-badge {
  font-size: 0.625rem;
  padding: 1px 6px;
  border-radius: var(--radius-sm, 0.25rem);
  background: var(--color-background-secondary);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
